import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Users, MapPin, MusicNotes, Cake, Heart } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MobileNav from '../components/MobileNav';
import BrandLogo from '../components/BrandLogo';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  {
    name: 'Photographers',
    value: 'photographer',
    icon: Camera,
    image: 'https://images.pexels.com/photos/26975878/pexels-photo-26975878.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    name: 'Venues',
    value: 'venue',
    icon: MapPin,
    image: 'https://images.pexels.com/photos/12919431/pexels-photo-12919431.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    name: 'Caterers',
    value: 'caterer',
    icon: Cake,
    image: 'https://images.pexels.com/photos/35446444/pexels-photo-35446444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    name: 'Decorators',
    value: 'decorator',
    icon: Heart,
    image: 'https://images.pexels.com/photos/35446444/pexels-photo-35446444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    name: 'DJs & Entertainment',
    value: 'dj',
    icon: MusicNotes,
    image: 'https://images.pexels.com/photos/35446444/pexels-photo-35446444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-sand-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="md" linkTo="/" />
          <MobileNav
            links={[
              { to: '/instant-pricing', label: t('home.get_instant_pricing'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-brand-primary hover:text-[#A84A38] transition-colors', testId: 'header-pricing-link' },
            ]}
            actions={user ? [
              { to: '/dashboard', label: t('common.dashboard') || 'Dashboard', className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-dashboard-link' },
              { onClick: logout, label: t('common.logout'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-logout-btn' },
            ] : [
              { to: '/login', label: t('common.login'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-login-link' },
              { to: '/register', label: t('common.register'), className: 'bg-brand-primary text-sand-50 rounded-none px-6 py-3 font-medium hover:bg-[#A84A38] transition-colors', testId: 'header-register-btn' },
            ]}
          />
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-[600px] flex items-center justify-center">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(253,251,247,0.95)), url('https://static.prod-images.emergentagent.com/jobs/4bb44e35-40a0-455b-9295-27fd264374cb/images/6dcad82c82d564fa14a7c4ea44bf15f41cdf3025749c9caea6a8a61cfeec5b30.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 text-center max-w-4xl px-4 sm:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none text-text-primary mb-4 sm:mb-6"
          >
            {t('home.hero_title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-sm sm:text-base leading-relaxed tracking-normal text-text-secondary mb-6 sm:mb-8 max-w-2xl mx-auto"
          >
            {t('home.hero_subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex gap-3 sm:gap-4 justify-center flex-wrap"
          >
            <Link to="/instant-pricing" className="bg-brand-primary text-sand-50 rounded-none px-5 sm:px-8 py-3 sm:py-4 font-medium hover:bg-[#A84A38] transition-colors text-sm sm:text-base" data-testid="hero-instant-pricing-btn">
              {t('home.get_instant_pricing')}
            </Link>
            {!user && (
              <Link
                to="/register"
                className="bg-transparent border border-text-primary text-text-primary rounded-none px-8 py-4 font-medium hover:bg-text-primary hover:text-sand-50 transition-colors"
                data-testid="hero-register-btn"
              >
                Join BeeWeddy
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-8 md:px-12 lg:px-16 bg-sand-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium leading-tight text-text-primary mb-12 text-center">
            Browse by Category
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.value}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={`/instant-pricing?category=${category.value}`}
                    className="block bg-transparent border border-[#E8E1D5] hover:-translate-y-1 transition-transform duration-300 overflow-hidden"
                    data-testid={`category-card-${category.value}`}
                  >
                    <div className="relative h-64">
                      <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-text-primary/80 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-sand-50">
                        <Icon size={32} weight="duotone" className="mb-2" />
                        <h3 className="text-2xl sm:text-3xl font-heading font-medium">{category.name}</h3>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-8 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium leading-tight text-text-primary mb-12 text-center">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: '01', title: 'Pick Your Event', desc: 'Choose your event type and share a few details to start building your estimate.' },
              { step: '02', title: 'See Instant Pricing', desc: 'View transparent pricing with detailed packages—no hidden costs.' },
              { step: '03', title: 'Book with Confidence', desc: 'Secure your booking and enjoy stress-free event planning.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-4">{item.step}</div>
                <h3 className="text-2xl sm:text-3xl font-heading font-medium mb-4">{item.title}</h3>
                <p className="text-base leading-relaxed tracking-normal text-text-secondary">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
